import type React from "react";
import { Suspense, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { SWRConfig } from "swr";
import { CanonicalLink } from "./components/common/CanonicalLink";
import { CookieConsentBanner } from "./components/cookie-consent/CookieConsentBanner";
import { CookiePreferencesModal } from "./components/cookie-consent/CookiePreferencesModal";
import { CookieSettingsButton } from "./components/cookie-consent/CookieSettingsButton";
import ErrorBoundary from "./components/error/ErrorBoundary";
import Layout from "./components/layout/Layout";
import LoadingSpinner from "./components/ui/loading-spinner";
import { Toaster } from "./components/ui/toaster";
import { TooltipProvider } from "./components/ui/tooltip";
import { AuthProvider } from "./contexts/AuthProvider";
import { PrivateNotesProvider } from "./contexts/PrivateNotesContext";
import { ServiceArticlesProvider } from "./contexts/ServiceArticlesContext";
import { useAnalytics } from "./hooks/useAnalytics";
import { useBfcacheRecovery } from "./hooks/useBfcacheRecovery";
import { useCatalogCache } from "./hooks/useCatalogCache";
import { useDarkModeSync } from "./hooks/useDarkModeSync";
import { fetcher } from "./lib/api";
import { lazyWithReload } from "./lib/lazy-with-reload";
import { useAuthStore } from "./stores/auth";
import { useCookieConsent } from "./stores/useCookieConsent";

// ページコンポーネントをlazy importでコード分割
const AdminServiceRequestsPage = lazyWithReload(() => import("./pages/AdminServiceRequestsPage"));
const AdminServicesPage = lazyWithReload(() => import("./pages/AdminServicesPage"));
const AdminBannersPage = lazyWithReload(() => import("./pages/AdminBannersPage"));
const AdminUsersPage = lazyWithReload(() => import("./pages/AdminUsersPage"));
const AdminApiKeysPage = lazyWithReload(() => import("./pages/AdminApiKeysPage"));
const ArchivePage = lazyWithReload(() => import("./pages/ArchivePage"));
const DevelopersPage = lazyWithReload(() => import("./pages/DevelopersPage"));
const ForgotPasswordPage = lazyWithReload(() => import("./pages/ForgotPasswordPage"));
const FaqPage = lazyWithReload(() => import("./pages/FaqPage"));
const FeedbackPage = lazyWithReload(() => import("./pages/FeedbackPage"));
const HomePage = lazyWithReload(() => import("./pages/HomePage"));
const LandingPage = lazyWithReload(() => import("./pages/LandingPage"));
const LoginPage = lazyWithReload(() => import("./pages/LoginPage"));
const NotFoundPage = lazyWithReload(() => import("./pages/NotFoundPage"));
const CommercialDisclosurePage = lazyWithReload(
  () => import("./pages/legal/CommercialDisclosurePage"),
);
const RefundPolicyPage = lazyWithReload(() => import("./pages/legal/RefundPolicyPage"));
const PricingPage = lazyWithReload(() => import("./pages/PricingPage"));
const PrivacyPolicyPage = lazyWithReload(() => import("./pages/PrivacyPolicyPage"));
const PublicDeckPage = lazyWithReload(() => import("./pages/PublicDeckPage"));
const RegisterPage = lazyWithReload(() => import("./pages/RegisterPage"));
const ResetPasswordPage = lazyWithReload(() => import("./pages/ResetPasswordPage"));
const ServiceDetailPage = lazyWithReload(() => import("./pages/ServiceDetailPage"));
const ServicesPage = lazyWithReload(() => import("./pages/ServicesPage"));
const SettingsPage = lazyWithReload(() => import("./pages/SettingsPage"));
const TermsPage = lazyWithReload(() => import("./pages/TermsPage"));
const VerifyEmailPage = lazyWithReload(() => import("./pages/VerifyEmailPage"));

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isReady = useAuthStore((s) => s.isReady);

  if (!isReady) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirect if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isReady = useAuthStore((s) => s.isReady);

  if (!isReady) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Admin Route Component (admin only)
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isReady = useAuthStore((s) => s.isReady);
  const isAdmin = useAuthStore((state) => state.user?.role === "admin");

  if (!isReady) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="rounded-lg border border-destructive bg-destructive/10 p-6 text-center">
          <p className="text-lg font-semibold text-destructive">アクセス権限がありません</p>
          <p className="mt-2 text-sm text-muted-foreground">この画面は管理者のみアクセスできます</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// SWRConfig 配下で bfcache 復元時のエラー回復フックを動かすための null コンポーネント
const BfcacheRecovery = () => {
  useBfcacheRecovery();
  return null;
};

// SWRConfig 配下でカタログキャッシュの永続化・先読みフックを動かすための null コンポーネント
const CatalogCache = () => {
  useCatalogCache();
  return null;
};

// GA4 の初期化・同意連動・ルート遷移トラッキングを動かすための null コンポーネント
const Analytics = () => {
  useAnalytics();
  return null;
};

function App() {
  const { initialize, checkExpiry } = useCookieConsent();

  // 背景色(isDark)に応じて html.dark を自動同期
  useDarkModeSync();

  // Cookie同意の初期化
  useEffect(() => {
    initialize();

    // 定期的に有効期限チェック（1時間ごと）
    const interval = setInterval(
      () => {
        checkExpiry();
      },
      60 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, [initialize, checkExpiry]);

  return (
    <SWRConfig
      value={{
        fetcher,
        revalidateOnFocus: false, // フォーカス時の再検証を無効化
        revalidateOnReconnect: false, // 再接続時の再検証を無効化
        dedupingInterval: 30000, // 30秒間は重複リクエストを排除
        errorRetryCount: 2, // エラー時のリトライ回数
        shouldRetryOnError: true, // エラー時にリトライする
        revalidateIfStale: false, // 古いデータがあっても再検証しない（手動mutateで制御）
      }}
    >
      <BfcacheRecovery />
      <CatalogCache />
      <Analytics />
      <CanonicalLink />
      <TooltipProvider delayDuration={200}>
        <AuthProvider>
          <PrivateNotesProvider>
            <ServiceArticlesProvider>
              <div className="min-h-screen bg-background">
                <Suspense
                  fallback={
                    <main className="min-h-screen flex items-center justify-center">
                      <h1 className="sr-only">ページを読み込み中</h1>
                      <LoadingSpinner />
                    </main>
                  }
                >
                  <Routes>
                    {/* Landing Page (Public) */}
                    <Route
                      path="/"
                      element={
                        <PublicRoute>
                          <LandingPage />
                        </PublicRoute>
                      }
                    />

                    {/* Public Routes */}
                    <Route
                      path="/login"
                      element={
                        <PublicRoute>
                          <LoginPage />
                        </PublicRoute>
                      }
                    />
                    <Route
                      path="/register"
                      element={
                        <PublicRoute>
                          <RegisterPage />
                        </PublicRoute>
                      }
                    />

                    {/* Password Reset (Public) */}
                    <Route
                      path="/forgot-password"
                      element={
                        <PublicRoute>
                          <ForgotPasswordPage />
                        </PublicRoute>
                      }
                    />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />

                    {/* Email Verification (Public) */}
                    <Route path="/verify-email" element={<VerifyEmailPage />} />

                    {/* Legal Pages (Public) */}
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/privacy" element={<PrivacyPolicyPage />} />
                    <Route path="/legal/commercial" element={<CommercialDisclosurePage />} />
                    <Route path="/legal/refund" element={<RefundPolicyPage />} />

                    {/* Info Pages (Public) */}
                    {/* /user-guide はコンテンツ側(apps/content, Astro)へ移行済み。SPA からは除去(素の <a> でフルページ遷移) */}
                    {/* /changelog はコンテンツ側(apps/content, Astro)へ移行済み。SPA からは除去(素の <a> でフルページ遷移) */}
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/faq" element={<FaqPage />} />
                    <Route path="/developers" element={<DevelopersPage />} />
                    <Route path="/archive" element={<ArchivePage />} />
                    <Route path="/feedback" element={<FeedbackPage />} />

                    {/* Public Deck (SNS シェア、認証不要) */}
                    <Route
                      path="/d/:slug"
                      element={
                        <ErrorBoundary variant="page">
                          <PublicDeckPage />
                        </ErrorBoundary>
                      }
                    />

                    {/* Protected Routes */}
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary variant="page">
                            <Layout>
                              <HomePage />
                            </Layout>
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/services"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary variant="page">
                            <Layout>
                              <ServicesPage />
                            </Layout>
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/services/:id"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary variant="page">
                            <Layout>
                              <ServiceDetailPage />
                            </Layout>
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />

                    {/* サービス紹介記事(旧 /articles)・/blog はコンテンツ側(apps/content, Astro)へ移行済み。SPA からは除去(素の <a> でフルページ遷移) */}

                    {/* Admin Routes */}
                    <Route
                      path="/admin/services"
                      element={
                        <AdminRoute>
                          <Layout>
                            <AdminServicesPage />
                          </Layout>
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/banners"
                      element={
                        <AdminRoute>
                          <Layout>
                            <AdminBannersPage />
                          </Layout>
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/users"
                      element={
                        <AdminRoute>
                          <Layout>
                            <AdminUsersPage />
                          </Layout>
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/service-requests"
                      element={
                        <AdminRoute>
                          <Layout>
                            <AdminServiceRequestsPage />
                          </Layout>
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/api-keys"
                      element={
                        <AdminRoute>
                          <Layout>
                            <AdminApiKeysPage />
                          </Layout>
                        </AdminRoute>
                      }
                    />

                    {/* Settings Route */}
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary variant="page">
                            <Layout>
                              <SettingsPage />
                            </Layout>
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />

                    {/* Catch all: 未知 URL は専用 404 ページへ（/dashboard への暗黙リダイレクトはしない） */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Suspense>
              </div>
              <Toaster />
              {/* Cookie同意UI */}
              <CookieConsentBanner />
              <CookiePreferencesModal />
              <CookieSettingsButton />
            </ServiceArticlesProvider>
          </PrivateNotesProvider>
        </AuthProvider>
      </TooltipProvider>
    </SWRConfig>
  );
}

export default App;
